import React, { useEffect, useMemo, useState } from 'react';
import numeral from 'numeral';
import style from 'assets/jss/material-dashboard-pro-react/components/fbbPlanDetailSummaryStyle';
import { makeStyles } from '@material-ui/core';
import { HeadingSBold, HeadingLBold, HeadingM, HeadingMBold, BodyM } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import { PhoneInTalkOutlined } from '@material-ui/icons';
import { calculateExpiryDate } from 'utils/date/getExpiryDate';
import { formatPrice } from 'utils/price';
import { useSelector } from 'react-redux';
import { getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';

// @ts-ignore
const useStyle = makeStyles(style);

interface IPlanDetailProps {
	line: types.services.fixed.fixedBusinessBroadband.LineDetails;
}

const PlanDetailSummary = (props: IPlanDetailProps) => {
	const [allStaticIp, setAllStaticIpOptions] = useState<types.ecommerce.CatalogEntryView[]>();

	const { line } = props;
	const fiberProLabels = useSelector(getFiberProLabels);
	const classes = useStyle();
	useEffect(() => {
		// Fetching static ip options
		ECommerceService.getGroupContent(idConfig.FBB.STATIC_IP[env]).subscribe(({ response }: any) => {
			if (response.catalogEntryView) {
				setAllStaticIpOptions(response.catalogEntryView);
			}
		});
	}, []);

	const hideStaticIpOption = (productId: string) => {
		let staticIpProduct = allStaticIp?.find((staticIp) => staticIp.partNumber === productId);
		if (staticIpProduct) {
			return extractAttributeValue(staticIpProduct, CustomAttribute.ORDER_SUMMARY_FEATURED, 'TRUE') === 'FALSE';
		}
		return false;
	};
	const getCostFromStaticIp = (field: string) => {
		return line.staticServicesList
			.filter((staticIp) => !memoizedHideStaticIpOption(staticIp.productId))
			.map((staticIp, index) => {
				return (
					<div className={classes.flexAndJustifyContexnt} key={index}>
						<HeadingM inline>{staticIp.productName}</HeadingM>
						<HeadingM inline>{translate('add.new.line.price', formatPrice(staticIp[field]))}</HeadingM>
					</div>
				);
			});
	};
	const memoizedHideStaticIpOption = useMemo(() => hideStaticIpOption, [allStaticIp]);
	return (
		<>
			<div className={classes.planDetail}>
				<div className={classes.titleSection}>
					<div>
						<span className={classes.title}>
							<HeadingLBold color="enterprise" inline>
								{`${line.productDetails.productName} `}
							</HeadingLBold>
						</span>
						<span className={classes.subTitle}>
							<HeadingSBold color="enterprise" inline>
								{line.productDetails.isFixedBroadband ? fiberProLabels.fiberPro : fiberProLabels.fbb}
							</HeadingSBold>
						</span>
					</div>
					<div className="">
						<HeadingM inline>
							{translate('add.new.line.price', formatPrice(line.productDetails.cost))}
						</HeadingM>
					</div>
				</div>
				<div className={classes.flexAndJustifyContexnt}>
					<div className={classes.contract}>
						<HeadingM inline>
							{translate('order.summary.contract.duration', line.productDetails.contractDuration)}
						</HeadingM>
					</div>
					<div className={classes.validity}>
						{calculateExpiryDate(+(line.productDetails.contractMonths ?? ''))}
					</div>
				</div>
				{/* <div className={classes.description}>
					<PhoneInTalkOutlined className={classes.phoneIcon} />
					<BodyM customClassName={classes.descriptionText}>
						{`  ` + line?.requiredServiceProductDetailsList[0]?.requiredServiceProductName}
					</BodyM>
				</div> */}

				{line.additionalServiceProductsDetailsList.length > 0 && (
					<>
						<div className={classes.addOnServices}>
							<BodyM>{translate('add.new.line.addon.service.title')}</BodyM>
						</div>
						{line.additionalServiceProductsDetailsList.map((addOn, index) => {
							return (
								<div className={classes.flexAndJustifyContexnt} key={index}>
									<HeadingM inline>{addOn.additionalServiceProductName}</HeadingM>
									<HeadingM inline>
										{translate(
											'add.new.line.price',
											formatPrice(addOn.additionalServiceMonthlyCost)
										)}
									</HeadingM>
								</div>
							);
						})}
					</>
				)}
				{line?.staticServicesList?.length > 0 && getCostFromStaticIp('monthlyCost')}
				<>
					<hr className={classes.summationHorizontalLine} />
					<div className={classes.flexAndJustifyContexnt}>
						<HeadingMBold inline>{translate('add.new.line.monthlycost')}</HeadingMBold>
						<HeadingMBold inline>
							{translate('add.new.line.price', formatPrice(line.monthlyCost))}
						</HeadingMBold>
					</div>
				</>
			</div>
			<div className={classes.installationDetail}>
				<div className={classes.installationTitle}>
					<p>{translate('add.new.line.installation.title')}</p>
				</div>
				{line?.additionalServiceProductsDetailsList?.length > 0 &&
					line.additionalServiceProductsDetailsList.map((addOn, index) => {
						return (
							<div className={classes.flexAndJustifyContexnt} key={index}>
								<HeadingM inline>{addOn.additionalServiceProductName}</HeadingM>
								<HeadingM inline>
									{translate(
										'add.new.line.price',
										formatPrice(addOn.additionalServiceInstallationCost)
									)}
								</HeadingM>
							</div>
						);
					})}
				{line?.staticServicesList?.length > 0 && getCostFromStaticIp('installationCost')}
				<div className={classes.flexAndJustifyContexnt}>
					<HeadingM inline>{line.productDetails.productName}</HeadingM>
					<HeadingM inline>
						{translate('add.new.line.price', formatPrice(line.productDetails.installationCost))}
					</HeadingM>
				</div>
				<hr className={classes.summationHorizontalLine} />
				<div className={classes.flexAndJustifyContexnt}>
					<HeadingMBold inline>{translate('add.new.line.installation.cost')}</HeadingMBold>
					<HeadingMBold inline>
						{translate('add.new.line.price', formatPrice(line.installationCost))}
					</HeadingMBold>
				</div>
			</div>
		</>
	);
};

export default PlanDetailSummary;
